import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ICriterion } from '../../../../distribute-team-members/models/criterion';
import { BaseComponent } from '../../../base.component';
import { CriterionsService } from '../../../services/criterions.service';
import { combineLatest, filter, finalize, forkJoin, iif, of, switchMap, takeUntil } from 'rxjs';
import { DecisionInput } from '../../../models/decision-input.model';
import { CriterionInformation } from '../../../models/criterion-information.mode';
import { showToast } from '../../../../toast.helper';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CopyModel } from '../../../models/copy.model';
import { ExecuteVisitType } from '../../../models/execute-visit.model';
import { FinalReportMistakes } from '../../../models/final-report-mistakes.model';
import { Category } from '../../../models/category.model';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'dp-formio';
import { ItimadCriterionsService } from '../../../services/itimad-criterions.service';

@Component({
  selector: 'app-criteria-decision',
  templateUrl: './criteria-decision.component.html',
  styleUrls: ['./criteria-decision.component.scss']
})
export class CriteriaDecisionComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() memberId: string;
  @Input() decisionPreview: boolean;
  isLoadingResults: boolean;
  form: FormGroup;
  finalReportMistakesForm: FormGroup;
  isSub = false;
  criterion: ICriterion;
  decisionInput: DecisionInput;
  modelId: number;
  mistakeId: number;

  category: Category;

  get isFinalReport() {
    return !this.decisionPreview && (this.decisionInput.executeVisitType == ExecuteVisitType.FinalReport ||
      this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries ||
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries);
  }

  get showInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries ||
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }

  get disabledInstitutionEntries() {
    return this.decisionInput.viewOnly ||
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }

  get isInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries;
  }

  get isFinalReportWithInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }
  get enabledCopy() {
    return !!this.memberId;
  }
  get isFormDisabled() {
    return !this.decisionInput.enableEdit ||
      this.enabledCopy ||
      this.isInstitutionEntries ||
      this.isFinalReportWithInstitutionEntries;
  }

  constructor(private formBuilder: FormBuilder,
    private criterionsService: CriterionsService,
    private itimadCriterionsService: ItimadCriterionsService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private matDialog: MatDialog) { super(); }

  ngOnInit() {
    combineLatest([this.criterionsService.criterion$, this.criterionsService.decisionInputs$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([criterion, inputs]) => {
        this.criterion = criterion;
        this.decisionInput = inputs;
        this.initForm();
        this.initFinalReportMistakesForm();
        this.getData();
        this.registerCopyEvaluation();
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.memberId?.previousValue && changes.memberId?.previousValue != changes.memberId?.currentValue) {
      this.getData();
    }
  }

  copy(controlName: string) {
    const value = this.form.controls[controlName].value;
    this.criterionsService.sendEvaluation$.next(new CopyModel(controlName, value));
  }

  submit() {
    if (this.form.valid) {
      this.isLoadingResults = true;
      const value = this.form.value;

      const data: CriterionInformation = {
        id: this.modelId,
        criterionId: this.criterion.id,
        criterionKey: this.criterion.criterionKey,
        visitKey: this.decisionInput.visitMetaData.visitKey,
        memberId: this.decisionInput.visitMetaData.memberId,
        ...value
      };

      of(!!this.modelId)
        .pipe(
          switchMap((isUpdate) =>
            iif(
              () => isUpdate,
              this.itimadCriterionsService.editCriterionInformation(data, this.isFinalReport),
              this.itimadCriterionsService.createCriterionInformation(data, this.isFinalReport)
            )
          ),
          finalize(() => this.isLoadingResults = false)
        )
        .subscribe(model => {
          this._updateForm(model);
          this.criterionsService.decisionTaken$.next();
          showToast(this.toastrService,
            this.translateService.instant("forms.common.Criteria.Saved done"),
            this.translateService.instant("forms.common.Criteria.Saved Successfully"),
            false
          );
        });
    }
  }
  submitMistakes() {
    if (this.finalReportMistakesForm.valid) {
      this.isLoadingResults = true;
      const value = this.finalReportMistakesForm.value;

      const data: FinalReportMistakes = {
        id: this.mistakeId,
        criterionId: this.modelId,
        criterionKey: this.criterion.criterionKey,
        criterionType: this.criterion.criterionType,
        visitKey: this.decisionInput.visitMetaData.visitKey,
        ...value
      };

      of(!!this.mistakeId)
        .pipe(
          switchMap((isUpdate) =>
            iif(
              () => isUpdate,
              this.itimadCriterionsService.editFinalReportMistakes(data),
              this.itimadCriterionsService.createFinalReportMistakes(data)
            )
          ),
          finalize(() => this.isLoadingResults = false)
        )
        .subscribe(model => {
          this._updateFinalReportMistakesForm(model);
          this.criterionsService.hasMistake$.next(true);
          showToast(this.toastrService,
            this.translateService.instant("forms.common.Criteria.Saved done"),
            this.translateService.instant("forms.common.Criteria.Saved Successfully"),
            false
          );
        });
    }
  }
  deleteMistakes() {
    const dir = this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr';
    this.matDialog.open(ConfirmationDialogComponent, {
      direction: dir,
      data: {
        dir: dir,
        message: 'forms.common.Are you sure to delete this item',
        buttonText: {
          ok: 'forms.common.yes',
          cancel: 'forms.common.cancel',
        }
      },
      width: "40vw",
      panelClass: 'confirmation-dialog',
    })
      .afterClosed()
      .pipe(filter(data => !!data))
      .subscribe(() => {
        this.itimadCriterionsService.deleteFinalReportMistakes(this.mistakeId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this._updateFinalReportMistakesForm(null);
            this.criterionsService.hasMistake$.next(false);
            showToast(this.toastrService,
              this.translateService.instant("forms.common.Criteria.Delete Mistake"),
              this.translateService.instant("forms.common.Criteria.Delete Mistake Successfully"),
              false
            );
          });
      });
  }

  private initCategory(info: CriterionInformation) {
    this.category = {
      totalEvaluationCriteria: info?.totalEvaluationCriteria || 0,
      numberOfApplicable: info?.numberOfApplicable || 0,
      calculatedSavedScore: info?.numberOfApplicable ? (info?.totalEvaluationCriteria / info?.numberOfApplicable) : 0
    };
  }

  private initForm() {
    this.isSub = this.criterion.criterionType == 'SUB';
    this.form = this.formBuilder.group({
      notes: [{ value: '', disabled: this.isFormDisabled }],
      complianceEvaluation: [{ value: '', disabled: this.isFormDisabled }, this.isSub ? [Validators.required] : null],
    });
  }

  private initFinalReportMistakesForm() {
    this.finalReportMistakesForm = this.formBuilder.group({
      mistake: [{ value: '', disabled: this.disabledInstitutionEntries }, [Validators.required]],
      suggestedSolution: [{ value: '', disabled: this.disabledInstitutionEntries }, [Validators.required]],
      notes: [{ value: '', disabled: this.disabledInstitutionEntries }, [Validators.required]],
    });
  }

  private getData() {
    this.isLoadingResults = true;
    forkJoin([
      this.itimadCriterionsService.getCriterionInformationForMainCriterion(
        this.decisionInput.visitMetaData.visitKey,
        this.criterion.criterionKey,
        this.memberId || this.decisionInput.currentMemberId,
        this.isFinalReport),
      this.showInstitutionEntries ? this.itimadCriterionsService.getFinalReportMistakes(
        this.decisionInput.visitMetaData.visitKey,
        this.criterion.criterionKey) : of(null)
    ]).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoadingResults = false)
    )
      .subscribe(([criterionInformation, mistakes]) => {
        this.initCategory(criterionInformation);
        this._updateForm(criterionInformation);
        this._updateFinalReportMistakesForm(mistakes);
      });
  }
  private _updateForm(data: CriterionInformation) {
    this.modelId = data?.id;
    this.form.patchValue({
      notes: data?.notes,
      complianceEvaluation: data?.complianceEvaluation
    });
  }
  private _updateFinalReportMistakesForm(data: FinalReportMistakes) {
    this.mistakeId = data?.id;
    this.finalReportMistakesForm.patchValue({
      mistake: data?.mistake,
      suggestedSolution: data?.suggestedSolution,
      notes: data?.notes,
    });
  }
  private registerCopyEvaluation() {
    if (!this.enabledCopy)
      this.criterionsService.sendEvaluation$
        .pipe(
          filter(data => !!data),
          takeUntil(this.destroy$)
        )
        .subscribe(data => {
          this.form.patchValue({
            [data.key]: data.value
          });
        });

  }
}
